<template>
  <button
    class="skip-carousel sr-only"
    :aria-label="props.data.skipCarouselExplained"
    @click="skipCarousel"
  >
    {{ props.data.skipCarouselText }}
  </button>
  <div class="carousel pt-10" role="region" :aria-label="`${props.data.sliderExplained}`">
    <div ref="inner" :style="innerStyles" class="inner">
      <div class="card-wrapper" :style="wrapperStyles" aria-live="polite" role="list">
        <div v-for="card in cards" :key="card" class="slider-card" role="listitem">
          <p class="heading-sm">{{ card.quote }}</p>
          <p v-if="card.name_company" class="label-lg">{{ card.name_company }}</p>
        </div>
        <div v-for="card in cards" :key="card" class="slider-card" role="listitem">
          <p class="heading-sm">{{ card.quote }}</p>
          <p v-if="card.name_company" class="label-lg">{{ card.name_company }}</p>
        </div>
        <div v-for="card in cards" :key="card" class="slider-card" role="listitem">
          <p class="heading-sm">{{ card.quote }}</p>
          <p v-if="card.name_company" class="label-lg">{{ card.name_company }}</p>
        </div>
        <div v-for="card in cards" :key="card" class="slider-card center-card" role="listitem">
          <p class="heading-sm">{{ card.quote }}</p>
          <p v-if="card.name_company" class="label-lg">{{ card.name_company }}</p>
        </div>
        <div v-for="card in cards" :key="card" class="slider-card" role="listitem">
          <p class="heading-sm">{{ card.quote }}</p>
          <p v-if="card.name_company" class="label-lg">{{ card.name_company }}</p>
        </div>
        <div v-for="card in cards" :key="card" class="slider-card" role="listitem">
          <p class="heading-sm">{{ card.quote }}</p>
          <p v-if="card.name_company" class="label-lg">{{ card.name_company }}</p>
        </div>
        <div v-for="card in cards" :key="card" class="slider-card" role="listitem">
          <p class="heading-sm">{{ card.quote }}</p>
          <p v-if="card.name_company" class="label-lg">{{ card.name_company }}</p>
        </div>
      </div>
    </div>
  </div>
  <div class="pt-16 pb-4 pr-4 flex gap-x-2 justify-center md:justify-end">
    <button
      :aria-label="`${props.data.buttonPrevExplained}`"
      class="button button-small button-primary"
      @click="prev"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
      >
        <path
          d="M5.22792 8.99756L1 4.99992M1 4.99992L5.22792 1.00229M1 4.99992L9 4.99992"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <span class="hidden md:inline">{{ props.data.buttonTextPrev }}</span>
    </button>
    <button
      :aria-pressed="autoplayEnabled ? 'true' : 'false'"
      :aria-label="`${props.data.autoplayExplained}`"
      class="button button-small button-primary"
      @click="toggleAutoplay"
    >
      <svg
        v-if="autoplayEnabled"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 16C9.45 16 9 15.55 9 15V9C9 8.45 9.45 8 10 8C10.55 8 11 8.45 11 9V15C11 15.55 10.55 16 10 16ZM14 16C13.45 16 13 15.55 13 15V9C13 8.45 13.45 8 14 8C14.55 8 15 8.45 15 9V15C15 15.55 14.55 16 14 16Z"
          fill="white"
        />
      </svg>
      <svg
        v-else
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM9.5 14.67V9.33C9.5 8.54 10.38 8.06 11.04 8.49L15.19 11.16C15.8 11.55 15.8 12.45 15.19 12.84L11.04 15.51C10.38 15.94 9.5 15.46 9.5 14.67Z"
          fill="white"
        />
      </svg>
      {{ autoplayEnabled ? props.data.autoplayTextStop : props.data.autoplayTextStart }}
    </button>
    <button
      :aria-label="`${props.data.buttonNextExplained}`"
      class="button button-small button-primary"
      @click="next"
    >
      <span class="hidden md:inline">{{ props.data.buttonTextNext }}</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
      >
        <path
          d="M4.77208 1.00244L9 5.00008M9 5.00008L4.77208 8.99771M9 5.00008L1 5.00008"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </div>
  <button
    v-if="showEndButton"
    ref="endButton"
    class="skip-carousel sr-only"
    :aria-label="props.data.endCarouselExplained"
    @blur="hideEndButton"
  >
    {{ props.data.endCarouselText }}
  </button>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'

const props = defineProps<{
  slides: { quote: string; name_company: string }[]
  data: {
    sliderExplained: string
    buttonTextNext: string
    buttonNextExplained: string
    buttonTextPrev: string
    buttonPrevExplained: string
    autoplayExplained: string
    autoplayTextStart: string
    autoplayTextStop: string
    skipCarouselText: string
    skipCarouselExplained: string
    endCarouselText: string
    endCarouselExplained: string
  }
}>()

const cards = ref(props.slides || [])
const innerStyles = ref({})
const wrapperStyles = ref({})
const step = ref(0)
const transitioning = ref(false)
const inner = ref<HTMLElement | null>(null)
const autoplayEnabled = ref(false)
const autoplaySpeed = ref(1)
const currentPosition = ref(0)
let autoplayInterval: number | null = null
const showEndButton = ref(false)
const endButton = ref<HTMLElement | null>(null)

function skipCarousel() {
  showEndButton.value = true
  nextTick(() => {
    if (endButton.value) {
      endButton.value.focus()
    }
  })
}

function hideEndButton() {
  showEndButton.value = false
}

function setStep() {
  if (inner.value) {
    const card = inner.value.querySelector('.slider-card') as HTMLElement
    const cardWidth = card.offsetWidth
    const cardMargin =
      parseFloat(getComputedStyle(card).marginRight) + parseFloat(getComputedStyle(card).marginLeft)
    step.value = cardWidth + cardMargin

    const cardsInArray = cards.value.length
    const innerWidth = inner.value.offsetWidth
    const targetOffset = (3 * cardsInArray + 0.5) * step.value
    const leftPosition = innerWidth / 2 - targetOffset

    wrapperStyles.value = {
      position: 'relative',
      left: `${leftPosition}px`
    }
  }
}

function next() {
  if (transitioning.value) return
  transitioning.value = true
  currentPosition.value = 0

  moveLeft()
  afterTransition(() => {
    const card = cards.value.shift()
    if (card) cards.value.push(card)
    resetTranslate()
    transitioning.value = false
  })
  pauseAutoplay()
}

function prev() {
  if (transitioning.value) return
  transitioning.value = true
  currentPosition.value = 0

  moveRight()
  afterTransition(() => {
    const card = cards.value.pop()
    if (card) cards.value.unshift(card)
    resetTranslate()
    transitioning.value = false
  })
  pauseAutoplay()
}

function moveLeft() {
  innerStyles.value = {
    transform: `translateX(-${step.value}px)`,
    transition: 'transform 0.5s ease-in-out'
  }
}

function moveRight() {
  innerStyles.value = {
    transform: `translateX(${step.value}px)`,
    transition: 'transform 0.5s ease-in-out'
  }
}

function afterTransition(callback: () => void) {
  if (inner.value) {
    const listener = () => {
      callback()
      inner.value?.removeEventListener('transitionend', listener)
    }
    inner.value.addEventListener('transitionend', listener)
  }
}

function resetTranslate() {
  innerStyles.value = {
    transition: 'none',
    transform: 'translateX(0)'
  }
}

function resetAutoTranslate() {
  innerStyles.value = {
    transition: 'none',
    transform: `translateX(${currentPosition.value}px)`
  }
}

function autoplayFlow() {
  if (!autoplayEnabled.value) return
  currentPosition.value -= autoplaySpeed.value
  innerStyles.value = {
    transform: `translateX(${currentPosition.value}px)`,
    transition: 'none'
  }

  const firstCard = inner.value?.querySelector('.center-card')
  if (firstCard) {
    const firstCardRightEdge = firstCard.getBoundingClientRect().right

    if (firstCardRightEdge <= -10) {
      const shiftedCard = cards.value.shift()
      if (shiftedCard) cards.value.push(shiftedCard)
      currentPosition.value += step.value
      resetAutoTranslate()
    }
  }

  if (autoplayEnabled.value) {
    autoplayInterval = requestAnimationFrame(autoplayFlow)
  }
}

function startAutoplay() {
  autoplayEnabled.value = true
  autoplayFlow()
}

function stopAutoplay() {
  autoplayEnabled.value = false
  if (autoplayInterval) cancelAnimationFrame(autoplayInterval)
}

function pauseAutoplay() {
  stopAutoplay()
}

function toggleAutoplay() {
  if (autoplayEnabled.value) {
    stopAutoplay()
  } else {
    startAutoplay()
  }
}

onMounted(() => {
  nextTick(() => {
    setStep()
    resetTranslate()
    startAutoplay()
  })
  window.addEventListener('resize', setStep)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', setStep)
  stopAutoplay()
})
</script>

<style scoped>
.carousel {
  width: 100%;
  overflow: hidden;
}

.inner {
  transition: transform 0.5s ease-in-out;
  white-space: nowrap;
  position: relative;
}

.card-wrapper {
  top: 0;
}

.skip-carousel:focus {
  position: static;
  width: auto;
  height: auto;
  clip: auto;
  overflow: visible;
  white-space: normal;
  border: none;
  background: #000;
  color: #fff;
  padding: 10px;
  border-radius: 4px;
  z-index: 1000;
}

.slider-card {
  width: 300px;
  margin-right: 48px;
  margin-left: 48px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  row-gap: 16px;
  text-align: center;
  white-space: normal;
  word-wrap: break-word;
}

@media (min-width: 640px) {
  .slider-card {
    width: 500px;
  }
}

@media (min-width: 1024px) {
  .slider-card {
    width: 800px;
  }
}
</style>
